import { useCallback, useEffect } from "react";

import { isInIframe } from "helpers/app";

type UseMessenger<TOutcomingMessage> = {
  sendMessage: (message: TOutcomingMessage) => void;
};

export const useMessenger = <TIncomingMessage, TOutcomingMessage>(
  handler: (message: TIncomingMessage) => void
): UseMessenger<TOutcomingMessage> => {
  const embeddedInIframe = isInIframe();

  const sendMessage = useCallback(
    (message: TOutcomingMessage) => {
      if (!embeddedInIframe) return;

      window.parent.postMessage(message, "*");
    },
    [embeddedInIframe]
  );

  const messageListener = useCallback(
    (event: MessageEvent<TIncomingMessage>) => {
      if (!embeddedInIframe) return;

      handler(event.data);
    },
    [handler]
  );

  useEffect(() => {
    if (!embeddedInIframe) return;

    window.addEventListener("message", messageListener);

    return (): void => {
      window.removeEventListener("message", messageListener);
    };
  }, [messageListener, embeddedInIframe]);

  return { sendMessage };
};
